import React, { useMemo, useEffect, useRef } from "react";
import { X, Loader2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@musa.tech/design-system";
import { DateRangePicker } from "@/components/shared/filters/DateRangePicker";
import { MultiSelect } from "@/components/dashboard/MultiSelect";
import { EstablishmentMultiSelect } from "@/components/dashboard/EstablishmentMultiSelect";
import { useFilterOptions } from "@/hooks/useFilterOptions";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEstablishmentName, maskGeneratorName } from "@/utils/demoMode";
import type { FilterState } from "@/hooks/useFilters";

/**
 * Prefixo do nome do evento de filtro (GR-712, RN-01 `pagina_acao`).
 * Este componente é compartilhado por Dashboard, Próximas e Histórico — o prefixo
 * é o que separa as três telas nos relatórios do GA4.
 */
type AnalyticsPrefix = "dashboard" | "proximas_coletas" | "historico_coletas";

interface FiltersBarProps {
  filters: FilterState;
  onDateRangeChange: (dateRange: { start: Date; end: Date }) => void;
  onMaterialsChange: (materials: string[]) => void;
  onStatesChange: (states: string[]) => void;
  onEstablishmentsChange: (establishments: string[]) => void;
  onGeneratorsChange: (generators: string[]) => void;
  onClearFilters: () => void;
  hasActiveFilters?: boolean;
  /** Oculta o seletor de período (ex.: em Próximas Coletas, onde o calendário controla a data). */
  hideDateRange?: boolean;
  /** Exibe o filtro "tem MTR" (ex.: no Histórico de Coletas). */
  showHasMtrFilter?: boolean;
  onHasMtrChange?: (hasMtr: boolean | null) => void;
  /** Identifica a tela no registro de uso de filtro (GR-712). */
  analyticsPrefix: AnalyticsPrefix;
  /** Revalidando dados em background após mudança de filtro (dados anteriores seguem na tela). */
  isFetching?: boolean;
}

export const FiltersBar = ({
  filters,
  onDateRangeChange,
  onMaterialsChange,
  onStatesChange,
  onEstablishmentsChange,
  onGeneratorsChange,
  onClearFilters,
  hasActiveFilters = false,
  hideDateRange = false,
  showHasMtrFilter = false,
  onHasMtrChange,
  analyticsPrefix,
  isFetching = false,
}: FiltersBarProps) => {
  const { trackButtonClick } = useAnalytics();

  /**
   * Dispara na escolha efetiva do valor, não na abertura do seletor (GR-712, D-01),
   * com um nome por campo (D-06).
   *
   * Os wrappers abaixo são usados **somente** nos componentes de UI. O
   * `onEstablishmentsChange` cru continua sendo chamado direto pelo efeito que
   * remove estabelecimentos inválidos — instrumentar ali registraria uma escolha
   * que o usuário não fez.
   */
  const trackFilter = (field: string) =>
    trackButtonClick({ buttonLabel: `${analyticsPrefix}_filtrar_${field}` });

  const handleDateRangeChange = (dateRange: { start: Date; end: Date }) => {
    trackFilter("periodo");
    onDateRangeChange(dateRange);
  };

  const handleMaterialsChange = (materials: string[]) => {
    trackFilter("residuo");
    onMaterialsChange(materials);
  };

  const handleStatesChange = (states: string[]) => {
    trackFilter("estado");
    onStatesChange(states);
  };

  const handleEstablishmentsChange = (establishments: string[]) => {
    trackFilter("estabelecimento");
    onEstablishmentsChange(establishments);
  };

  const handleGeneratorsChange = (generators: string[]) => {
    trackFilter("gerador");
    onGeneratorsChange(generators);
  };

  const handleHasMtrChange = (hasMtr: boolean | null) => {
    trackFilter("mtr");
    onHasMtrChange?.(hasMtr);
  };

  const { establishments, states, generators, materialOptions } =
    useFilterOptions({
      selectedGeneratorExternalIds: filters?.generators,
    });
  const { isDemoMode } = useDemoMode();

  const showGeneratorFilter = generators.length > 1;
  const filterCount =
    (hideDateRange ? 0 : 1) +
    3 +
    (showGeneratorFilter ? 1 : 0) +
    (showHasMtrFilter ? 1 : 0);
  const gridColsMap: Record<number, string> = {
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  };
  const gridCols = gridColsMap[filterCount] ?? "lg:grid-cols-4";

  // Referência para rastrear geradores anteriores e evitar loops
  const prevGeneratorsRef = useRef<string[]>(filters.generators);

  // Filtrar estabelecimentos baseado no gerador selecionado
  const filteredEstablishments = useMemo(() => {
    // Se nenhum gerador selecionado, mostrar todos
    if (filters.generators.length === 0) {
      return establishments;
    }

    return establishments;
  }, [establishments, filters.generators]);

  // Limpar estabelecimentos selecionados que não pertencem ao gerador escolhido
  useEffect(() => {
    // Só executar se os geradores realmente mudaram
    const generatorsChanged =
      prevGeneratorsRef.current.length !== filters.generators.length ||
      !prevGeneratorsRef.current.every((g) => filters.generators.includes(g));

    if (!generatorsChanged) return;

    prevGeneratorsRef.current = filters.generators;

    // Se nenhum gerador selecionado, não precisa limpar
    if (filters.generators.length === 0) return;

    // Verificar se algum estabelecimento selecionado não pertence aos geradores
    const validEstablishmentIds = new Set(
      filteredEstablishments.map((est) => est.establishment_id),
    );

    const invalidSelections = filters.establishments.filter(
      (estId) => !validEstablishmentIds.has(estId),
    );

    // Remover estabelecimentos inválidos automaticamente
    if (invalidSelections.length > 0) {
      const validSelections = filters.establishments.filter((estId) =>
        validEstablishmentIds.has(estId),
      );
      onEstablishmentsChange(validSelections);
    }
  }, [
    filters.generators,
    filters.establishments,
    filteredEstablishments,
    onEstablishmentsChange,
  ]);

  // Mapear para opções do componente
  const establishmentOptions = useMemo(() => {
    return filteredEstablishments.map((est) => ({
      value: est.establishment_id,
      label: maskEstablishmentName(est.establishment_name, isDemoMode),
    }));
  }, [filteredEstablishments, isDemoMode]);

  return (
    <Card className="mb-4 md:mb-6 w-full overflow-hidden bg-primary-50">
      <CardContent className="p-3 md:p-4">
        <div className="flex flex-col space-y-3 md:space-y-4">
          {/* Header with clear button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Text variant="heading" size="small" className="text-neutral-900">
                Filtros
              </Text>
              {isFetching && (
                <span
                  className="flex items-center gap-1.5 rounded-full bg-white border border-primary-200 px-2.5 py-1 text-xs font-medium text-neutral-900 shadow-sm"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary-600" />
                  Atualizando...
                </span>
              )}
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
              >
                <X className="h-4 w-4 mr-2" />
                Limpar filtros
              </Button>
            )}
          </div>

          {/* Filter controls */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-3 md:gap-4`}
          >
            {/* Date Range */}
            {!hideDateRange && (
              <div className="space-y-2">
                <Text variant="label" size="small" className="block text-neutral-700">
                  Período
                </Text>
                <DateRangePicker
                  value={filters.dateRange}
                  onChange={handleDateRangeChange}
                  className="w-full"
                />
              </div>
            )}

            {/* Material Type */}
            <div className="space-y-2">
              <Text variant="label" size="small" className="block text-neutral-700">
                Tipo de Resíduo
              </Text>
              <MultiSelect
                options={materialOptions}
                value={filters.materials}
                onChange={handleMaterialsChange}
                placeholder="Todos os materiais"
                aria-label="Filtro de tipo de resíduo"
                searchPlaceholder="Buscar material..."
                emptyMessage="Nenhum material encontrado."
              />
            </div>

            {/* State (UF) */}
            <div className="space-y-2">
              <Text variant="label" size="small" className="block text-neutral-700">
                Estado (UF)
              </Text>
              <MultiSelect
                options={states}
                value={filters.states}
                onChange={handleStatesChange}
                placeholder="Todos os estados"
                aria-label="Filtro de estado"
                searchPlaceholder="Buscar estado..."
                emptyMessage="Nenhum estado encontrado."
              />
            </div>

            {/* Establishment */}
            <div className="space-y-2">
              <Text variant="label" size="small" className="block text-neutral-700">
                Estabelecimento
              </Text>
              <EstablishmentMultiSelect
                options={establishmentOptions}
                value={filters.establishments}
                onChange={handleEstablishmentsChange}
                placeholder={
                  filters.generators.length > 0
                    ? `${filteredEstablishments.length} estabelecimento${filteredEstablishments.length !== 1 ? "s" : ""} do gerador`
                    : "Todos os estabelecimentos"
                }
                aria-label="Filtro de estabelecimento"
              />
            </div>

            {/* Generator - exibido quando o usuário tem acesso a mais de um gerador */}
            {showGeneratorFilter && (
              <div className="space-y-2">
                <Text variant="label" size="small" className="block text-neutral-700">
                  Gerador
                </Text>
                <MultiSelect
                  options={generators.map((gen) => ({
                    value: gen.generator_id,
                    label: maskGeneratorName(gen.generator_name, isDemoMode),
                  }))}
                  value={filters.generators}
                  onChange={handleGeneratorsChange}
                  placeholder="Todos os geradores"
                  aria-label="Filtro de gerador"
                  searchPlaceholder="Buscar gerador..."
                  emptyMessage="Nenhum gerador encontrado."
                />
              </div>
            )}

            {/* Tem MTR */}
            {showHasMtrFilter && (
              <div className="space-y-2">
                <Text variant="label" size="small" className="block text-neutral-700">MTR</Text>
                <Select
                  value={
                    filters.hasMtr === null
                      ? "all"
                      : filters.hasMtr
                        ? "com"
                        : "sem"
                  }
                  onValueChange={(value) =>
                    handleHasMtrChange(value === "all" ? null : value === "com")
                  }
                >
                  <SelectTrigger aria-label="Filtro de MTR" className="bg-white">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="com">Com MTR</SelectItem>
                    <SelectItem value="sem">Sem MTR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
